<template>
  <NebulaPageShell
    v-if="user"
    :current-site="currentSite"
    :home-link="homeUrl"
    :global-nav-data="waffleData"
    :nav-context="null"
    :product-bar-data="{
      homeLogoElement: 'a',
      homeLink: homeUrl,
      pageTitle: $t('Studio'),
      userInitials,
      avatarImage,
    }"
    :profile-panel-data="{
      isUserImpersonating: userIsImpersonating,
      learnUrl: domains.appDomain,
      userAvatarUrl: avatarImage,
      userBackgroundUrl: profileBackgroundImage,
      userFullName,
      userHasProfileLink,
      quitImpersonateUrl,
    }"
    :quick-list-total-count="quicklistIds.length"
    show-quick-list-toggle
    :switch-site-url="switchSiteUrl"
    @toggle-global-nav="loadWaffleData"
  >
    <template #search>
      <NebulaProductBarSearch
        v-if="showSearch"
        :action="searchAction"
        search-input-id="nebula-product-bar__search-input"
        :lang-code="localeCode"
        :recently-searched="recentlySearched"
        :suggestions="searchSuggestions"
        :user-suggestions="userSearchSuggestions"
        :type-list="[]"
      >
        <template #searchInput="{ emitInputKeyDownEvents }">
          <NebulaInput
            id="nebula-product-bar__search-input"
            aria-autocomplete="list"
            aria-activedescendant=""
            aria-controls="nebula-product-bar__suggestions-list"
            autocomplete="off"
            class="nebula-product-bar__search-input"
            name="q"
            :placeholder="$t('Search')"
            size="s"
            @keydown="emitInputKeyDownEvents"
            @keyup="debouncedUpdateSearchSuggestions"
          />
        </template>
      </NebulaProductBarSearch>
    </template>

    <template #quickListSlot>
      <!-- The draggable prop can be switched to true after STUD-2920 is complete -->
      <NebulaQuickList
        :draggable="false"
        :quick-list="quicklist.quicklist"
        :quick-list-total-count="quicklistIds.length"
        :reorder-handler="reorderQuicklist"
        @onLoadMoreButtonClick="loadQuicklistNextPage"
      >
        <template #quickListActionsMenu>
          <NebulaDropdown button-id="quicklist-action-menu">
            <template
              #trigger="{
                toggleDropdown,
                checkArrowKeyOnTrigger,
                checkArrowKeyAfterTrigger,
              }"
            >
              <NebulaButton
                id="quicklist-action-menu"
                icon-left="ellipses"
                shape="pill"
                size="s"
                type="flat"
                haspopup="true"
                :is-disabled="quicklistIds.length === 0"
                @click="toggleDropdown"
                @keydown="checkArrowKeyOnTrigger"
                @keyup="checkArrowKeyAfterTrigger"
              />
            </template>
            <template #content="{ toggleDropdown }">
              <NebulaDropdownItem
                icon="bookmark"
                :text="$t('Add to My Content')"
                data-gac="Link"
                data-gaa="Button"
                data-gal="QuickList|Action|BulkAdd"
                @dropdown-item-clicked="openBulkMyContentModal"
              />
              <NebulaDropdownItem
                icon="circle-minus"
                :text="$t('Remove All Items')"
                data-gac="Link"
                data-gaa="Button"
                data-gal="QuickList|Action|RemoveAll"
                @dropdown-item-clicked="removeAllItemsFromQuicklist(toggleDropdown)"
              />
            </template>
          </NebulaDropdown>
        </template>
        <template #quickListItemActions="{ asset }">
          <ActionMenu
            :asset="asset"
            :learn-url="domains.appDomain"
            :quicklist-ids="quicklistIds"
            :show-create-with-studio="false"
            :subscribed-channel-ids="channelIds"
            :user-is-student="userIsStudent"
            @quicklist:remove="removeItemFromQuicklist(asset)"
            @save="openMyContentModal(asset)"
            @share="openShareModal(asset)"
            @subscribe="subscribe(asset)"
            @unsubscribe="unsubscribe(asset)"
          />
        </template>
      </NebulaQuickList>
    </template>

    <slot />
  </NebulaPageShell>
</template>

<script>
import {
  NebulaButton,
  NebulaDropdown,
  NebulaDropdownItem,
  NebulaInput,
  NebulaPageShell,
  NebulaProductBarSearch,
  NebulaQuickList,
} from '@discoveryedu/nebula-components';
import { ActionMenu, profileAvatars, profileBackgrounds } from '@discoveryedu/platform-components';
import { debounce, get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  PRODUCT_SETTING_ENABLE_STUDENT_PROFILE,
  PRODUCT_SETTING_HIDE_PROFILE,
  PRODUCT_SETTING_SHOW_SEARCH,
  PRODUCT_SETTING_STUDENT_SEARCH,
} from '@/lib/constants';
import getSearchSuggestions from '@/lib/utils/search-suggest';
import experimentData from '@/mixins/experimentData';
import {
  useChannelStore,
  useModalStore,
  useQuicklistStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'NebulaLayout',
  components: {
    ActionMenu,
    NebulaButton,
    NebulaDropdown,
    NebulaDropdownItem,
    NebulaInput,
    NebulaPageShell,
    NebulaProductBarSearch,
    NebulaQuickList,
  },
  mixins: [experimentData],
  data() {
    return {
      lastSearchSuggestTerm: null,
      recentlySearched: [],
      searchSuggestions: [],
      userSearchSuggestions: [],
      waffleData: {},
      waffleDataLoaded: false,
    };
  },
  computed: {
    ...mapState(useUserStore, [
      'region',
      'user',
      'userIsImpersonating',
      'userIsStudent',
      'domains',
      'productSettings',
    ]),
    ...mapState(useChannelStore, [
      'channelIds',
    ]),
    ...mapState(useQuicklistStore, [
      'quicklist',
      'quicklistIds',
    ]),
    avatarImage() {
      const profileImage = !this.user?.is_shared_student_account && this.user.image;
      if (profileImage) return profileImage;

      const avatarId = this.user?.theme?.THEMECHARACTER;
      return profileAvatars.avatarImagePath(
        avatarId || 'robot03',
        this.domains.appDomain.replace('/learn', '/static/'),
      );
    },
    currentSite() {
      return (this.user.multisites && this.user.site) || null;
    },
    homeUrl() {
      return new URL('/', this.domains.appDomain).toString();
    },
    localeCode() {
      return get(this, 'user.analytics_data.locale.code', 'en');
    },
    profileBackgroundImage() {
      const themeId = this.user?.theme?.THEMECOLOR;
      if (!themeId) return null;

      return profileBackgrounds.themeImagePath(
        themeId,
        this.domains.appDomain.replace('/learn', '/static/'),
      );
    },
    quitImpersonateUrl() {
      return `${this.domains.adminDomain}/users/unimpersonate`;
    },
    searchAction() {
      let searchUrl = `${this.domains.appDomain}/search`;
      const searchExperiment = this.getExperimentData('search-phase-1-experiment');

      if (searchExperiment?.is_member && !searchExperiment?.is_control) {
        searchUrl = `${this.domains.appDomain}/search-2023`;
      }
      return searchUrl;
    },
    showSearch() {
      if (this.userIsStudent) {
        return get(this.productSettings, `${PRODUCT_SETTING_SHOW_SEARCH}.value`)
          && get(this.productSettings, `${PRODUCT_SETTING_STUDENT_SEARCH}.value`);
      }
      return get(this.productSettings, `${PRODUCT_SETTING_SHOW_SEARCH}.value`);
    },
    switchSiteUrl() {
      return `${this.domains.appDomain}/profile/switch_site?next=${window.location.href}`;
    },
    userFullName() {
      return `${get(this.user, 'first_name') || ''} ${get(this.user, 'last_name') || ''}`;
    },
    userHasProfileLink() {
      // HIDEPROFILE hides the profile for everyone
      if (get(this.productSettings, `${PRODUCT_SETTING_HIDE_PROFILE}.value`)) return false;

      // Profile is hidden for shared student accounts
      if (this.user.is_shared_student_account) return false;

      // Students must have ENABLESTUDENTPROFILE to have a profile
      if (this.userIsStudent
        && !get(this.productSettings, `${PRODUCT_SETTING_ENABLE_STUDENT_PROFILE}.value`)) return false;

      return true;
    },
    userInitials() {
      return this.user ? `${this.user.first_name.charAt(0)}${this.user.last_name.charAt(0)}` : null;
    },
  },
  created() {
    this.$deApi.get('/user_history/recently_searched')
      .then((response) => {
        this.recentlySearched = response.data.recently_searched_history.terms;
      })
      .catch((err) => {
        this.recentlySearched = err;
      });

    this.debouncedUpdateSearchSuggestions = debounce(
      this.updateSearchSuggestions,
      250,
      { trailing: true },
    );
  },
  methods: {
    ...mapActions(useQuicklistStore, [
      types.LOAD_MORE_QUICKLIST,
      types.MOVE_QUICKLIST_ITEM,
      types.REMOVE_FROM_QUICKLIST,
    ]),
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
      types.SET_MY_CONTENT_MODAL_OPTIONS,
    ]),
    ...mapActions(useChannelStore, [
      types.SUBSCRIBE_TO_CHANNEL,
      types.UNSUBSCRIBE_FROM_CHANNEL,
    ]),
    loadQuicklistNextPage() {
      this[types.LOAD_MORE_QUICKLIST]();
    },
    loadWaffleData() {
      // Prevent duplicate requests
      if (this.waffleDataLoaded) return;

      this.waffleDataLoaded = true;
      this.$deApi.get('/navigation/waffle/data', {
        withCredentials: true,
      }).then((response) => {
        this.waffleData = response.data;
      });
    },
    openMyContentModal(asset) {
      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        asset: {
          actions: [],
          selectedAssetId: asset.id,
          selectedAssetTitle: asset.title || asset.name,
          sharingEnabled: true,
        },
        config: {
          hideDistrictTab: this.region === 'UK', // see DEX-2646
          isActive: true,
          isStudent: this.userIsStudent,
          modalType: 'save',
        },
      });
    },
    openShareModal(asset) {
      this[types.OPEN_MODAL]({
        type: 'AssetShareModal',
        id: 'AssetShareModal',
        assetUrl: `${this.domains.appDomain}/player/${asset.id}`,
        assetGuid: asset.id,
        headline: asset.name,
        description: asset.description || '',
      });
    },
    openBulkMyContentModal() {
      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        asset: {
          actions: [],
          bulk: {
            assets: this.quicklistIds,
            token: this.quicklist.tokens.add_many_to_folder,
          },
          sharingEnabled: true,
        },
        config: {
          hideDistrictTab: this.region === 'UK', // see DEX-2646
          isActive: true,
          isStudent: this.userIsStudent,
          modalType: 'save',
        },
      });
    },
    subscribe(asset) {
      this[types.SUBSCRIBE_TO_CHANNEL](asset);
    },
    unsubscribe(asset) {
      this[types.UNSUBSCRIBE_FROM_CHANNEL](asset);
    },
    removeAllItemsFromQuicklist(toggleDropdown) {
      this[types.OPEN_MODAL]({
        type: 'RemoveAllQuicklistItemsModal',
        onConfirm: toggleDropdown,
      });
    },
    removeItemFromQuicklist(asset) {
      this[types.REMOVE_FROM_QUICKLIST](asset);
    },
    reorderQuicklist(reorderedList, newIndex, itemId) {
      this[types.MOVE_QUICKLIST_ITEM]({
        items: reorderedList,
        position: newIndex,
        itemId,
      });
    },
    async updateSearchSuggestions(e) {
      const term = e.target.value;
      const suggestions = await getSearchSuggestions(term, this.$deApi);

      if (suggestions?.data) {
        this.searchSuggestions = suggestions.data;
        this.userSearchSuggestions = suggestions.user_suggestions;
      } else {
        // Clear out suggestions dropdown
        this.searchSuggestions = [];
        this.userSearchSuggestions = [];
        this.lastSearchSuggestTerm = term;
      }
    },
  },
};
</script>

<style lang="stylus">
// Prevent any page content from appearing over navs or panels
.nebula-page-shell__content {
    z-index: 1;
}

.nebula-global-nav {
    position: fixed;
}

.nebula-global-nav,
.nebula-profile-panel {
    z-index: 150;
}

.nebula-product-bar,
.nebula-global-nav__overlay,
.nebula-profile-panel__overlay {
    z-index: 149;
}
</style>
